import { FC, KeyboardEvent, MouseEvent, useState } from 'react';
import Popover from '@material-ui/core/Popover';
import { makeStyles } from '@material-ui/core/styles';
import { IAccountNetworkSummary } from 'models/Account';

/**
 * MUI v4 rather than v5 on purpose: material-table renders inside the v4 theme and makeStyles tree,
 * so a v5 popover in one of its cells picks up inconsistent z-index and styling.
 */
const useStyles = makeStyles({
    badge: {
        marginLeft: 6,
        padding: '1px 6px',
        borderRadius: 10,
        border: '1px solid #c4c8d4',
        backgroundColor: '#f2f4f8',
        color: '#14172c',
        fontSize: 11,
        lineHeight: '16px',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
    },
    popover: { padding: '10px 14px', maxWidth: 260 },
    title: { margin: '0 0 6px', color: '#6b7085', fontSize: 11, textTransform: 'uppercase' },
    // The list scrolls, not the whole popover, so the heading stays visible. Accounts can own
    // dozens of networks, which otherwise runs the popover off the bottom of the screen.
    list: {
        margin: 0,
        padding: 0,
        paddingRight: 6, // keeps the scrollbar off the network names
        listStyle: 'none',
        maxHeight: 300,
        overflowY: 'auto',
        // Matches the app-wide scrollbar in layouts/main (5px, #14172c track, #fe0072 thumb).
        // Repeated here rather than left to the global `*` rule so Firefox, which ignores
        // ::-webkit-scrollbar, is themed too.
        scrollbarWidth: 'thin' as const,
        scrollbarColor: '#fe0072 #14172c',
        '&::-webkit-scrollbar': { width: 5, height: 5, backgroundColor: '#14172c' },
        '&::-webkit-scrollbar-thumb': { backgroundImage: 'linear-gradient(to right,#fe0072,#fe0072)', outline: '1px solid slategrey' },
    },
    network: { fontSize: 12, lineHeight: '20px', whiteSpace: 'nowrap' },
    networkName: { color: '#6b7085', marginLeft: 6 },
});

interface NetworksBadgeProps {
    networks: IAccountNetworkSummary[];
}

const NetworksBadge: FC<NetworksBadgeProps> = ({ networks }) => {
    const classes = useStyles();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    // Table rows navigate to the account view on click, so every interaction here has to stop
    // propagating or opening the popover would leave the page.
    const open = (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchor(event.currentTarget);
    };

    // Popover's onClose is (event: {}, reason) => void, so it takes no typed event of its own; it
    // fires on backdrop click and on Escape, and neither needs propagation stopped — the backdrop
    // sits above the row, so the click never reaches it.
    const close = () => setAnchor(null);

    const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            setAnchor(event.currentTarget);
        }
    };

    return (
        <>
            <span className={classes.badge} role="button" tabIndex={0} aria-label={`Show all ${networks.length} networks on this account`} onClick={open} onKeyDown={onKeyDown}>
                +{networks.length - 1} networks
            </span>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <div className={classes.popover} onClick={(event) => event.stopPropagation()}>
                    <p className={classes.title}>Networks on this account</p>
                    <ul className={classes.list}>
                        {networks.map((network) => (
                            <li key={network.id} className={classes.network}>
                                {network.id}
                                {network.name && <span className={classes.networkName}>{network.name}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            </Popover>
        </>
    );
};

export default NetworksBadge;
